import { loadVideoAdsState, updateVideoAdsState } from '../storage/video_ads_storage.js';

export type AdsInterstitialRange = {
  min_level: number;
  every_n_levels: number;
};

export type AdsInterstitialConfig = {
  enabled: boolean;
  ranges: AdsInterstitialRange[];
};

export const DEFAULT_INTERSTITIAL_CONFIG: AdsInterstitialConfig = {
  enabled: true,
  ranges: [{ min_level: 30, every_n_levels: 5 }],
};

function asRecord( value: unknown ): Record<string, unknown> | null
{
  return value != null && typeof value === 'object' && !Array.isArray( value )
    ? value as Record<string, unknown>
    : null;
}

function numberValue( value: unknown, fallback: number, min_value = 0 ): number
{
  const n = typeof value === 'number' ? value : Number( value );
  if ( !Number.isFinite( n ) ) return fallback;
  return Math.max( min_value, Math.floor( n ) );
}

export function normalizeInterstitialRanges( inter: Record<string, unknown> ): AdsInterstitialRange[]
{
  const fallback = DEFAULT_INTERSTITIAL_CONFIG.ranges[0];

  if ( Array.isArray( inter.ranges ) && inter.ranges.length > 0 )
  {
    const ranges = inter.ranges
      .map( value => asRecord( value ) )
      .filter( ( value ): value is Record<string, unknown> => value !== null )
      .map( range => ( {
        min_level: numberValue( range.min_level, 0 ),
        every_n_levels: numberValue( range.every_n_levels, fallback.every_n_levels, 1 ),
      } ) )
      .sort( ( a, b ) => a.min_level - b.min_level );

    if ( ranges.length > 0 ) return ranges;
  }

  // Legacy config becomes one unbounded range.
  return [{
    min_level: numberValue( inter.min_level ?? inter.min_games, fallback.min_level ),
    every_n_levels: numberValue( inter.every_n_levels ?? inter.interstitial_every_n_wins, fallback.every_n_levels, 1 ),
  }];
}

export function interstitialRangeFor( config: AdsInterstitialConfig, level: number ): AdsInterstitialRange | null
{
  if ( !config.enabled ) return null;
  return [...config.ranges].reverse().find( range => level >= range.min_level ) ?? null;
}

export function isInterstitialDue( config: AdsInterstitialConfig, level: number, levels_passed: number ): boolean
{
  const range = interstitialRangeFor( config, level );
  return range !== null && levels_passed >= range.every_n_levels;
}

export function interstitialLevelsPassed(): number
{
  return loadVideoAdsState().inter_levels_since_last_show ?? 0;
}

export function countInterstitialLevel( config: AdsInterstitialConfig, level: number ): number
{
  if ( !interstitialRangeFor( config, level ) ) return interstitialLevelsPassed();

  return updateVideoAdsState( current => ( {
    ...current,
    inter_levels_since_last_show: ( current.inter_levels_since_last_show ?? 0 ) + 1,
  } ) ).inter_levels_since_last_show ?? 0;
}

export function resetInterstitialLevels(): void
{
  updateVideoAdsState( current => ( { ...current, inter_levels_since_last_show: 0 } ) );
}
